import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../services/api/client.js';

/*
  Admin "Refresh activation" — internal ops tool. Enter an ALREADY-ACTIVE user's
  email and force-regenerate their content strategy (in place, free) + first post
  (old archived, fresh one built on the new strategy). Everything else (account,
  billing, connections, scheduled/published posts, library) stays.

  Durable: the work runs in a content-worker queue op, so this view survives a
  reload — the pending operation id is kept in sessionStorage and re-polled on
  mount. Status + outcome are read from the authoritative async_operations row.

  Security is 100% server-side (auth-worker requireAdmin); this screen carries no
  client guard, mirroring AdminBroadcast.
*/

const STORE_KEY = 'dathent.activation.lastOp';
const POLL_MS = 2000;
const TERMINAL = new Set(['succeeded', 'failed']);

function loadStored() {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function store(value) {
  try {
    if (value) sessionStorage.setItem(STORE_KEY, JSON.stringify(value));
    else sessionStorage.removeItem(STORE_KEY);
  } catch { /* ignore */ }
}

function failureCopy(reason) {
  switch (reason) {
    case 'research_in_progress':
      return 'Market research is running for this user right now — try again in a few minutes.';
    case 'STRATEGY_WORKER_BINDING_MISSING':
      return 'Strategy service is unavailable.';
    case null:
    case undefined:
    case '':
      return 'Something went wrong.';
    default:
      return String(reason);
  }
}

const C = {
  page: { maxWidth: 640, margin: '0 auto', padding: '8px 4px 48px' },
  card: { border: '1px solid var(--border, #2a2a30)', borderRadius: 14, padding: 20, background: 'var(--surface, #16161a)', marginTop: 16 },
  label: { display: 'block', fontSize: 13, color: 'var(--text-muted, #9a9aa2)', marginBottom: 6 },
  input: { width: '100%', boxSizing: 'border-box', padding: '11px 12px', borderRadius: 10, border: '1px solid var(--border, #2a2a30)', background: 'var(--bg, #0f0f12)', color: 'var(--text, #e9e9ee)', fontSize: 15 },
  btn: (disabled) => ({ marginTop: 14, padding: '11px 18px', borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1, background: 'var(--gold, #caa24a)', color: '#1a1408' }),
  note: { fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)', marginTop: 10, lineHeight: 1.5 },
  err: { marginTop: 12, padding: '10px 12px', borderRadius: 10, border: '1px solid color-mix(in srgb, #e5484d 45%, transparent)', background: 'color-mix(in srgb, #e5484d 12%, transparent)', color: '#ffb4b6', fontSize: 13.5 },
  row: { display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13.5, padding: '7px 0', borderTop: '1px solid var(--border, #2a2a30)' },
  k: { color: 'var(--text-muted, #9a9aa2)' },
  v: { color: 'var(--text, #e9e9ee)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5, wordBreak: 'break-all', textAlign: 'right' },
};

function StatusPill({ status }) {
  const map = {
    running: { bg: 'color-mix(in srgb, #caa24a 16%, transparent)', fg: '#e7c878', label: 'Running…' },
    succeeded: { bg: 'color-mix(in srgb, #30a46c 16%, transparent)', fg: '#7ee2a8', label: 'Succeeded' },
    failed: { bg: 'color-mix(in srgb, #e5484d 14%, transparent)', fg: '#ffb4b6', label: 'Failed' },
  };
  const s = map[status] || map.running;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '4px 11px', borderRadius: 999, background: s.bg, color: s.fg, fontSize: 12.5, fontWeight: 600 }}>
      {status === 'running' && <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'currentColor', opacity: 0.9 }} />}
      {s.label}
    </span>
  );
}

export function AdminActivation() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [op, setOp] = useState(null);
  const [opMeta, setOpMeta] = useState(loadStored());
  const timerRef = useRef(null);

  const poll = useCallback(async (operationId) => {
    try {
      const { operation } = await api.adminActivation.getOperation(operationId);
      setOp(operation);
      return Boolean(operation && TERMINAL.has(operation.status));
    } catch (e) {
      // 404 (op expired/cleared) or network — surface as a terminal failure.
      setOp((cur) => cur ?? { status: 'failed', errorMessage: e?.message || 'poll_failed', metadata: null });
      return true;
    }
  }, []);

  useEffect(() => {
    if (!opMeta?.operationId) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const done = await poll(opMeta.operationId);
      if (!cancelled && !done) timerRef.current = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { cancelled = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, [opMeta, poll]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || submitting) return;
    setSubmitting(true);
    setError(null);
    setOp(null);
    try {
      const { operationId } = await api.adminActivation.refresh(value);
      const meta = { operationId, email: value, at: new Date().toISOString() };
      store(meta);
      setOpMeta(meta);
    } catch (err) {
      setError(err?.message || 'Request failed');
      setOpMeta(null);
      store(null);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setOpMeta(null);
    setOp(null);
    store(null);
    setError(null);
  };

  const status = op?.status ?? (opMeta?.operationId && !op ? 'running' : null);
  const running = status === 'running' || submitting;
  const meta = op?.metadata && typeof op.metadata === 'object' ? op.metadata : null;
  const failureReason = meta?.failureReason ?? op?.errorMessage ?? null;

  return (
    <div style={C.page}>
      <p style={{ fontSize: 13.5, color: 'var(--text-muted, #9a9aa2)', lineHeight: 1.6, margin: '4px 0 0' }}>
        Force-refresh an <strong style={{ color: 'var(--text, #e9e9ee)' }}>already-active</strong> user's content
        plan + first post (e.g. after a manual re-onboarding pulled fresh brand
        data). Regenerates the strategy in place and replaces the first post; the
        account, billing, connections, scheduled/published posts and library are
        untouched.
      </p>

      <form onSubmit={onSubmit} style={C.card}>
        <label style={C.label} htmlFor="act-email">User email</label>
        <input
          id="act-email"
          type="email"
          autoComplete="off"
          spellCheck={false}
          placeholder="user@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={C.input}
          disabled={running}
        />
        <button type="submit" style={C.btn(running || !email.trim())} disabled={running || !email.trim()}>
          {submitting ? 'Starting…' : running ? 'Refreshing…' : 'Refresh activation'}
        </button>
        <p style={C.note}>
          Only works for users who have completed onboarding (have an active content plan). Non-active users are rejected.
        </p>
        {error && <div style={C.err}>{error}</div>}
      </form>

      {opMeta?.operationId && (
        <div style={C.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <div style={{ fontSize: 12.5, color: 'var(--text-muted, #9a9aa2)' }}>{opMeta.email}</div>
              <div style={{ marginTop: 6 }}><StatusPill status={status || 'running'} /></div>
            </div>
            {status && TERMINAL.has(status) && (
              <button type="button" onClick={reset} style={{ background: 'transparent', border: '1px solid var(--border, #2a2a30)', color: 'var(--text-muted, #9a9aa2)', borderRadius: 9, padding: '7px 13px', fontSize: 13, cursor: 'pointer' }}>
                Run another
              </button>
            )}
          </div>

          {status === 'running' && (
            <p style={C.note}>Regenerating the strategy, then the first post. This survives a reload — you can leave and come back.</p>
          )}

          {status === 'succeeded' && (
            <div style={{ marginTop: 14 }}>
              <div style={{ ...C.row, borderTop: 'none' }}><span style={C.k}>Strategy regenerated</span><span style={C.v}>{meta?.strategyId || '✓'}</span></div>
              <div style={C.row}><span style={C.k}>New first post</span><span style={C.v}>{meta?.firstPostNewId || '✓'}</span></div>
              <div style={C.row}><span style={C.k}>Old post archived</span><span style={C.v}>{meta?.firstPostArchivedId || '— (none)'}</span></div>
            </div>
          )}

          {status === 'failed' && (
            <div style={{ ...C.err, marginTop: 14 }}>{failureCopy(failureReason)}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminActivation;
